/**
 * DRI Insights page – BU/individual DRI views, certifications, skill badges, training summary.
 * Types, utilities, constants, and MetricCard are in types/driInsights, utils, constants, and MetricCard.tsx.
 */

import React, { type ReactNode } from 'react';
import { Box, Card, CardContent, Tooltip, Typography, alpha } from '@mui/material';
import {
  METRIC_CARD_APPLE_RADIUS,
  METRIC_CARD_TITLE_FONT_SIZE,
  METRIC_CARD_TITLE_LINE_HEIGHT,
  METRIC_CARD_TITLE_MARGIN_BOTTOM,
  metricCardAppleShellSx,
} from '../cards/metricCardTheme';
import { SEARCE_COLORS } from '../../theme';
import { metricSummaryTooltipProps } from '../../constants/metricTooltips';

export interface DRIInsightsMetricCardProps {
  title: string;
  value: string | number;
  icon?: React.ElementType;
  color?: string;
  onClick?: () => void;
  /** Extra context on hover (same pattern as Utilization Insights metric cards). */
  tooltip?: ReactNode;
}

/**
 * Metric card for DRI Insights – individual metric display with optional icon and click.
 * Reusable within DRI/certification views.
 */
const MetricCard = ({
  title,
  value,
  icon: Icon,
  color = SEARCE_COLORS.primary,
  onClick,
  tooltip,
}: DRIInsightsMetricCardProps) => {
  const getColorScheme = (colorKey: string) => {
    switch (colorKey) {
      case SEARCE_COLORS.primary:
        return { bg: SEARCE_COLORS.primary_600, text: SEARCE_COLORS.on_primary, hover: SEARCE_COLORS.primary_700 };
      case SEARCE_COLORS.success:
        return { bg: SEARCE_COLORS.success_600, text: SEARCE_COLORS.on_success, hover: SEARCE_COLORS.success_700 };
      case SEARCE_COLORS.warning:
        return { bg: SEARCE_COLORS.warning_600, text: SEARCE_COLORS.on_warning, hover: SEARCE_COLORS.warning_700 };
      case SEARCE_COLORS.error:
        return { bg: SEARCE_COLORS.error_600, text: SEARCE_COLORS.on_error, hover: SEARCE_COLORS.error_700 };
      case SEARCE_COLORS.info:
        return { bg: SEARCE_COLORS.info_600, text: SEARCE_COLORS.on_info, hover: SEARCE_COLORS.info_700 };
      default:
        return { bg: SEARCE_COLORS.neutral_100, text: SEARCE_COLORS.on_neutral_100, hover: SEARCE_COLORS.neutral_200 };
    }
  };

  const colorScheme = getColorScheme(color);

  const card = (
    <Card
      elevation={0}
      onClick={onClick}
      sx={[
        {
          height: 88,
          width: '100%',
          background: colorScheme.bg,
          cursor: onClick ? 'pointer' : 'default',
          overflow: 'hidden',
          position: 'relative',
        },
        metricCardAppleShellSx(colorScheme.hover),
      ]}
    >
      <CardContent
        sx={{
          p: 1.5,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          justifyContent: 'center',
          borderRadius: METRIC_CARD_APPLE_RADIUS,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: METRIC_CARD_TITLE_MARGIN_BOTTOM,
            minHeight: 24,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: alpha(colorScheme.text, 0.9),
              fontSize: METRIC_CARD_TITLE_FONT_SIZE,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              lineHeight: METRIC_CARD_TITLE_LINE_HEIGHT,
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Typography
            className="metric-value"
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: '0.95rem',
              color: colorScheme.text,
              mb: 0,
              lineHeight: 1.1,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              textAlign: 'center',
              width: '100%',
            }}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} {...metricSummaryTooltipProps}>
        <Box sx={{ width: '100%', display: 'block', borderRadius: METRIC_CARD_APPLE_RADIUS }}>{card}</Box>
      </Tooltip>
    );
  }

  return card;
};

export default MetricCard;
